/**
 * Scale system for SkyTrek.
 *
 * Real solar-system sizes span many orders of magnitude. We keep accurate
 * relative radii and true AU ordering, then apply mild logarithmic compression
 * on orbital distance beyond the inner system so free-roam stays usable.
 */

/** Earth radii (km) — base unit for body sizes in the scene. */
export const EARTH_RADIUS_KM = 6371;

/** 1 AU in km. */
export const AU_KM = 149_597_870.7;

/**
 * Scene units per Earth radius.
 * Earth mesh radius ≈ this value.
 */
export const SCENE_PER_EARTH_RADIUS = 1;

/** Convert km radius → scene units. */
export function radiusKmToScene(km: number): number {
  return (km / EARTH_RADIUS_KM) * SCENE_PER_EARTH_RADIUS;
}

/**
 * Hybrid orbital distance mapping:
 * - Nearly linear inside ~2.5 AU (inner system feel preserved)
 * - Log compression beyond that (outer planets reachable)
 */
const LINEAR_AU = 2.5;
const COMPRESS_STRENGTH = 0.42;
/** Scene units corresponding to 1 AU at origin of the mapping. */
export const AU_SCENE = 2800;

export function auToScene(au: number): number {
  const a = Math.abs(au);
  let mapped: number;
  if (a <= LINEAR_AU) {
    mapped = a;
  } else {
    const extra = a - LINEAR_AU;
    mapped = LINEAR_AU + Math.log1p(extra) * (LINEAR_AU * COMPRESS_STRENGTH + 1.15);
  }
  return Math.sign(au || 1) * mapped * AU_SCENE;
}

/** Inverse for display (approx). */
export function sceneToAu(sceneDist: number): number {
  const s = Math.abs(sceneDist) / AU_SCENE;
  if (s <= LINEAR_AU) return Math.sign(sceneDist || 1) * s;
  // Newton-ish inverse of log branch
  let a = LINEAR_AU + 1;
  for (let i = 0; i < 8; i++) {
    const mapped =
      LINEAR_AU + Math.log1p(a - LINEAR_AU) * (LINEAR_AU * COMPRESS_STRENGTH + 1.15);
    const deriv =
      (LINEAR_AU * COMPRESS_STRENGTH + 1.15) / (1 + (a - LINEAR_AU));
    a -= (mapped - s) / Math.max(deriv, 1e-6);
    a = Math.max(LINEAR_AU + 1e-6, a);
  }
  return Math.sign(sceneDist || 1) * a;
}

export function formatDistance(sceneDist: number): string {
  const au = sceneToAu(sceneDist);
  const km = Math.abs(au) * AU_KM;
  if (km < 10_000) return `${km.toFixed(0)} km`;
  if (km < 1_000_000) return `${(km / 1000).toFixed(1)} × 10³ km`;
  if (Math.abs(au) < 0.01) return `${(km / 1e6).toFixed(2)} M km`;
  if (Math.abs(au) < 10) return `${au.toFixed(3)} AU`;
  return `${au.toFixed(2)} AU`;
}

export function formatSpeed(sceneSpeed: number): string {
  // Map scene units/s to a readable tier label + numeric
  const auPerSec = sceneSpeed / AU_SCENE;
  const kmPerSec = auPerSec * AU_KM;
  if (kmPerSec < 1) return `${(kmPerSec * 1000).toFixed(0)} m/s`;
  if (kmPerSec < 1000) return `${kmPerSec.toFixed(1)} km/s`;
  if (auPerSec < 0.01) return `${(kmPerSec / 1000).toFixed(2)} × 10³ km/s`;
  return `${auPerSec.toFixed(4)} AU/s`;
}
